import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  /** Current dashboard metadata */
  dashboardId: null,
  dashboardName: "",

  /** Canvas state — kept in Redux for cross-component access */
  nodes: [],
  edges: [],
  viewport: { x: 0, y: 0, zoom: 1 },
  background: { variant: "dots", color: "#ffffff", patternColor: "#e2e8f0", gap: 20, size: 1 },

  /** Dirty flag — tracks unsaved changes */
  isDirty: false,
  saveTriggered: false, // Tracks if header Save was clicked
  saving: false,        // Tracks if saving is in progress

  /** Component config modal state */
  configModal: {
    open: false,
    mode: "create", // "create" | "edit"
    componentType: null,
    nodeData: null,
  },

  /** Component selection modal state */
  showComponentSelect: false,
};

const liveDashboardSlice = createSlice({
  name: "liveDashboard",
  initialState,
  reducers: {
    /** Initialize canvas from API response */
    initCanvas: (state, action) => {
      const { id, name, nodes, edges, viewport, background } = action.payload;
      state.dashboardId = id;
      state.dashboardName = name || "";
      state.nodes = nodes || [];
      state.edges = edges || [];
      state.viewport = viewport || initialState.viewport;
      state.background = background || initialState.background;
      state.isDirty = false;
    },

    /** Sync nodes from React Flow onChange */
    setNodes: (state, action) => {
      state.nodes = action.payload;
      state.isDirty = true;
    },

    /** Sync edges from React Flow onChange */
    setEdges: (state, action) => {
      state.edges = action.payload;
      state.isDirty = true;
    },

    /** Update viewport on pan/zoom */
    setViewport: (state, action) => {
      state.viewport = action.payload;
    },

    setShowComponentSelect: (state, action) => {
      state.showComponentSelect = action.payload;
    },

    /** Update background settings */
    setBackground: (state, action) => {
      state.background = { ...state.background, ...action.payload };
      state.isDirty = true;
    },

    /** Add a new node to canvas */
    addNode: (state, action) => {
      state.nodes.push(action.payload);
      state.isDirty = true;
    },

    /** Update a single node's data */
    updateNodeData: (state, action) => {
      const { nodeId, data } = action.payload;
      const idx = state.nodes.findIndex((n) => n.id === nodeId);
      if (idx !== -1) {
        state.nodes[idx] = {
          ...state.nodes[idx],
          data: { ...state.nodes[idx].data, ...data },
        };
        state.isDirty = true;
      }
    },

    /** Remove a node and its associated edges */
    removeNode: (state, action) => {
      const nodeId = action.payload;
      state.nodes = state.nodes.filter((n) => n.id !== nodeId);
      state.edges = state.edges.filter((e) => e.source !== nodeId && e.target !== nodeId);
      state.isDirty = true;
    },

    /** Add edge */
    addEdge: (state, action) => {
      state.edges.push(action.payload);
      state.isDirty = true;
    },

    /** Remove edge */
    removeEdge: (state, action) => {
      state.edges = state.edges.filter((e) => e.id !== action.payload);
      state.isDirty = true;
    },

    /** Mark canvas as saved */
    markClean: (state) => {
      state.isDirty = false;
    },

    /** Mark canvas as containing unsaved changes */
    markDirty: (state) => {
      state.isDirty = true;
    },

    triggerSave: (state) => {
      state.saveTriggered = true;
    },

    resetSaveTrigger: (state) => {
      state.saveTriggered = false;
    },

    setSaving: (state, action) => {
      state.saving = action.payload;
    },

    /** Open component config modal */
    openConfigModal: (state, action) => {
      state.configModal = {
        open: true,
        mode: action.payload.mode || "create",
        componentType: action.payload.componentType || null,
        nodeData: action.payload.nodeData || null,
      };
    },

    /** Close component config modal */
    closeConfigModal: (state) => {
      state.configModal = { ...initialState.configModal };
    },

    /** Reset slice */
    resetLiveDashboard: () => initialState,
  },
});

export const {
  initCanvas,
  setNodes,
  setEdges,
  setViewport,
  setBackground,
  addNode,
  updateNodeData,
  removeNode,
  addEdge,
  removeEdge,
  markClean,
  markDirty,
  triggerSave,
  resetSaveTrigger,
  setSaving,
  openConfigModal,
  closeConfigModal,
  setShowComponentSelect,
  resetLiveDashboard,
} = liveDashboardSlice.actions;

export default liveDashboardSlice;
